'use client';

import Skeleton from '@carvana/ds-skeleton';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavPayloadAction } from '@carvana/merch-native';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext, useMemo } from 'react';
import { ANALYTICS_EVENTS } from '~/constants/flp/analyticsEvents';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import { usePricingUpdate } from '~/context/flp-rsc/PricingUpdateContext';
import { DownPaymentAggregate } from '~/contracts/flp';
import { getCvnaIdFinanceFilters } from '~/utilities/flp/getCvnaId';
import {
  ButtonContainer,
  Copy,
  CopyDescription,
  CopyHeading,
  Shop
} from './ShopDown.styles';

type OwnProps = {
  downPaymentInfo: DownPaymentAggregate;
};

export default function ShopDownButton({ downPaymentInfo }: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { isPending } = usePricingUpdate();

  const url = useMemo(() => {
    const cvnaid = getCvnaIdFinanceFilters({
      downPayment: downPaymentInfo?.downPayment
    });

    return `/cars/filters?cvnaid=${cvnaid}&refSource=financing`;
  }, [downPaymentInfo?.downPayment]);

  const onClick = useCallback(() => {
    datadogRum.addAction(DATADOG_EVENTS.PREQUALIFIED.CLICKS_SHOPBYCASHDOWN, {
      ...downPaymentInfo
    });
    trackEvent(ANALYTICS_EVENTS.PREQUALIFIED.CLICKS_SHOPBYCASHDOWN, {
      ...downPaymentInfo
    });
  }, [downPaymentInfo, trackEvent]);

  return (
    <ButtonContainer
      data-analytics-id="Financing Shop Down Payment Button"
      data-native-payload={JSON.stringify({ action: NavPayloadAction.ViewSrp })}
      href={url}
      onClick={onClick}
      data-testid="shop-down-payment"
    >
      <Copy>
        {isPending ? (
          <>
            <Skeleton width={140} height={20} className="rounded-md" />
            <Skeleton width={90} height={16} className="rounded-md mt-4" />
          </>
        ) : (
          <>
            <CopyHeading>{`Under $${downPaymentInfo?.downPayment.toLocaleString(
              'en-US'
            )} down`}</CopyHeading>
            <CopyDescription>{`${downPaymentInfo?.count.toLocaleString(
              'en-US'
            )} vehicles`}</CopyDescription>
          </>
        )}
      </Copy>
      <Shop>Shop -&gt;</Shop>
    </ButtonContainer>
  );
}
